import { Navigate, createBrowserRouter, useParams } from "react-router";

import { routerBasename } from "./lib/auth-flow.js";
import { ManageLayout } from "./components/ManageLayout.js";
import { AppGate } from "./components/PlatformShell";
import {
	PlatformWorkspaceGate,
	WorkspaceLegacyRedirect,
	WorkspaceNestedPlatformRedirect,
	WorkspaceRootRedirect,
	WorkspaceScope,
} from "./components/WorkspaceScope.js";
import { WorkspaceHubIndexRedirect } from "./components/WorkspaceHubRedirect.js";
import { PlatformBillingPage, PlatformUsersPage } from "./routes/platform/PlatformAdminPages.js";
import { RequireAuth } from "./components/RequireAuth";
import { KeyAi } from "./routes/KeyAi";
import { Aliases } from "./routes/Aliases";
import { Forwarders } from "./routes/Forwarders";
import { Migration } from "./routes/Migration";
import { MigrationNew } from "./routes/MigrationNew";
import { NotFound } from "./routes/NotFound";
import { Overview } from "./routes/Overview";
import { AcceptInvitation } from "./routes/auth/AcceptInvitation";
import { ForgotPassword } from "./routes/auth/ForgotPassword";
import { Register } from "./routes/auth/Register";
import { ResetPassword } from "./routes/auth/ResetPassword";
import { SignIn } from "./routes/auth/SignIn";
import { TwoFactor } from "./routes/auth/TwoFactor";
import { VerifyEmail } from "./routes/auth/VerifyEmail";
import { DomainDetail } from "./routes/domains/DomainDetail";
import { DomainNew } from "./routes/domains/DomainNew";
import { DomainRemove } from "./routes/domains/DomainRemove";
import { DomainsList } from "./routes/domains/DomainsList";
import { MailboxDetail } from "./routes/mailboxes/MailboxDetail";
import { MailboxNew } from "./routes/mailboxes/MailboxNew";
import { MailboxesList } from "./routes/mailboxes/MailboxesList";
import { Settings } from "./routes/settings/Settings";
import { AbusePage, AuditPage, CaddyNote, MailHealthPage, QueuesPage, ServerSettingsPage } from "./routes/shell/PlatformPages";
import { HUB_PAGES, HubHome, HubLegacyWorkRedirect, HubMine, HubSection, HubStaffPageGate, HubTeamTasksGate, MY_HUB_PAGES, SiteDatabases, SiteList, SiteOverview, SiteRedirects } from "./routes/shell/RecordPages";
import { SiteFiles } from "./routes/shell/SiteFiles";
import { Billing } from "./routes/Billing";
import {
	AccessPage,
	ConfigPage,
	MailHome,
	WorkspaceAppsPage,
} from "./routes/shell/WorkspacePages";
import { ManageTeamParticipantsPage } from "./routes/settings/ManageTeamParticipants.js";
import { TeamChannels, TeamOverview } from "./routes/shell/TeamPages";
import { SocialCalendar, SocialChannels, SocialCompose, SocialOverview, SocialResults } from "./routes/shell/SocialPages";
import { EngageAccounts, EngageConversation, EngageInbox, EngageLinkedInSignIn, EngagePolicy } from "./routes/shell/EngagePages";
import {
	ClientCalendar,
	ClientHome,
	ClientInvoices,
	ClientQuotations,
	ClientWork,
} from "./routes/shell/ClientPages.js";
import { PhoneCalls, PhoneSms } from "./routes/shell/PhonePages.js";
import {
	ManageEmailHome,
	ManageHubPage,
	ManagePhoneHome,
	ManagePhoneNumbers,
	ManagePlaceholder,
} from "./routes/shell/ManagePages.js";
import {
	PlatformWorkspacePage,
	UserBillingPage,
	UserUsersPage,
	UserWorkspacesPage,
} from "./routes/user/UserPages.js";
import { legacyUserAccountRedirect } from "./lib/platform-navigation.js";

function Legacy({ to }: { to: string }) {
	const params = useParams();
	const rest = params["*"] ?? "";
	return <Navigate to={rest ? `${to}/${rest}` : to} replace />;
}

function LegacyUserAccount() {
	const params = useParams();
	const section = params["*"] ?? "";
	const target = legacyUserAccountRedirect(`/manage/account${section ? `/${section}` : ""}`);
	return <Navigate to={target ?? "/user/settings/profile"} replace />;
}

function LegacyWorkspaceApps() {
	const { organisationId } = useParams();
	return <Navigate to={`/platform/workspace/${organisationId ?? ""}`} replace />;
}

const handle = (title: string) => ({ handle: { title } });

const basename = routerBasename();

export const router = createBrowserRouter([
	{ path: "/signin", element: <SignIn /> },
	{ path: "/register", element: <Register /> },
	{ path: "/verify-email", element: <VerifyEmail /> },
	{ path: "/forgot-password", element: <ForgotPassword /> },
	{ path: "/reset-password", element: <ResetPassword /> },
	{ path: "/invite", element: <AcceptInvitation /> },
	{ path: "/2fa", element: <TwoFactor /> },
	{ path: "/manage/signin", element: <Navigate to="/signin" replace /> },
	{ path: "/manage/register", element: <Navigate to="/register" replace /> },
	{ path: "/mail/signin", element: <Navigate to="/signin" replace /> },
	{ path: "/dash/signin", element: <Navigate to="/signin" replace /> },
	{
		element: <RequireAuth />,
		children: [
			{ path: "/", element: <WorkspaceRootRedirect /> },
			{ path: "/ai", element: <KeyAi />, ...handle("Key AI") },
			{ path: "/user", element: <Navigate to="/user/workspaces" replace /> },
			{ path: "/user/settings", element: <Navigate to="/user/settings/profile" replace /> },
			{ path: "/user/settings/:section", element: <Settings />, ...handle("Profile & Settings") },
			{ path: "/user/workspaces", element: <UserWorkspacesPage />, ...handle("Workspace") },
			{ path: "/user/billing", element: <UserBillingPage />, ...handle("Billing") },
			{ path: "/user/users", element: <UserUsersPage />, ...handle("Users") },

			{
				path: "/platform",
				element: <PlatformWorkspaceGate />,
				children: [
					{ index: true, element: <Navigate to="workspace" replace /> },
					{ path: "workspace", element: <PlatformWorkspacePage />, ...handle("Workspace") },
					{ path: "workspace/settings", element: <ConfigPage />, ...handle("Workspace settings") },
					{
						path: "workspace/:organisationId",
						element: <WorkspaceAppsPage />,
						...handle("Apps"),
					},
					{ path: "users", element: <PlatformUsersPage />, ...handle("Users") },
					{ path: "billing", element: <PlatformBillingPage />, ...handle("Billing") },
					{
						path: "system",
						element: <Navigate to="system/mail/health" replace />,
					},
					{ path: "system/mail/health", element: <MailHealthPage />, ...handle("Health") },
					{ path: "system/mail/queues", element: <QueuesPage />, ...handle("Queues") },
					{ path: "system/mail/abuse", element: <AbusePage />, ...handle("Abuse") },
					{ path: "system/mail/server", element: <ServerSettingsPage />, ...handle("Server settings") },
					{ path: "system/audit", element: <AuditPage />, ...handle("Audit") },
					{
						path: "system/caddy/routes",
						element: <CaddyNote topic="Routes and HTTPS" />,
						...handle("Routes"),
					},
					{
						path: "system/caddy/proxy",
						element: <CaddyNote topic="Proxy settings" />,
						...handle("Proxy"),
					},
					{ path: "system/caddy/logs", element: <CaddyNote topic="Logs" />, ...handle("Logs") },
					{
						path: "system/caddy/service",
						element: <CaddyNote topic="The Caddy service" />,
						...handle("Service"),
					},
				],
			},

			{
				path: "/workspace/:workspaceSlug",
				element: <WorkspaceScope />,
				children: [
					{
						element: <AppGate app="hub" />,
						children: [{ index: true, element: <HubHome />, ...handle("Overview") }],
					},
					{ path: "platform/*", element: <WorkspaceNestedPlatformRedirect /> },
					{
						path: "manage",
						element: <ManageLayout />,
						children: [
							{ index: true, element: <Overview />, ...handle("Overview") },
							{ path: "access", element: <AccessPage />, ...handle("Access") },
							{ path: "billing", element: <Billing />, ...handle("Billing") },
							{
								path: "integration",
								element: <AppGate app="hub" />,
								children: [
									{ index: true, element: <ManageHubPage id="integration" />, ...handle("Integration") },
								],
							},
							{
								path: "activity",
								element: <AppGate app="hub" />,
								children: [
									{ index: true, element: <ManageHubPage id="activity" />, ...handle("Activity logs") },
								],
							},
							{
								path: "email",
								element: <AppGate app="mail" />,
								children: [
									{ index: true, element: <ManageEmailHome /> },
									{ path: "mailboxes", element: <MailboxesList />, ...handle("Mailboxes") },
									{ path: "mailboxes/new", element: <MailboxNew />, ...handle("New mailbox") },
									{ path: "mailboxes/:id", element: <MailboxDetail />, ...handle("Mailbox") },
									{ path: "aliases", element: <Aliases />, ...handle("Aliases") },
									{ path: "forwarders", element: <Forwarders />, ...handle("Forwarders") },
									{ path: "migration", element: <Migration />, ...handle("Migration") },
									{ path: "migration/new", element: <MigrationNew />, ...handle("Set up a migration") },
								],
							},
							{
								path: "phone",
								element: <AppGate app="hub" />,
								children: [
									{ index: true, element: <ManagePhoneHome /> },
									{ path: "numbers", element: <ManagePhoneNumbers />, ...handle("Numbers") },
									{
										path: "duty",
										element: (
											<ManagePlaceholder
												title="Duty & Availability"
												detail="On-call schedules and availability for phone routing are not in the control panel yet."
											/>
										),
										...handle("Duty & Availability"),
									},
									{
										path: "routing",
										element: (
											<ManagePlaceholder
												title="Call Routing"
												detail="Call routing rules are not in the control panel yet."
											/>
										),
										...handle("Call Routing"),
									},
									{
										path: "status",
										element: (
											<ManagePlaceholder
												title="System Status"
												detail="Live phone system status is not in the control panel yet."
											/>
										),
										...handle("System Status"),
									},
									{
										path: "audit",
										element: (
											<ManagePlaceholder
												title="Audit Log"
												detail="Phone audit history is not in the control panel yet. Workspace activity is under Activity logs."
											/>
										),
										...handle("Audit Log"),
									},
									{
										path: "notifications",
										element: (
											<ManagePlaceholder
												title="Platform Notifications"
												detail="Phone platform notifications are not in the control panel yet."
											/>
										),
										...handle("Platform Notifications"),
									},
								],
							},
							{
								path: "team",
								element: <AppGate app="team" />,
								children: [
									{ index: true, element: <ManageTeamParticipantsPage />, ...handle("Team") },
									{ path: "access", element: <Navigate to=".." replace /> },
								],
							},
							{ path: "domains", element: <DomainsList />, ...handle("Domains") },
							{ path: "domains/new", element: <DomainNew />, ...handle("Add domain") },
							{ path: "domains/:id", element: <DomainDetail />, ...handle("Domain") },
							{ path: "domains/:id/remove", element: <DomainRemove />, ...handle("Remove domain") },
							{ path: "workspaces", element: <Navigate to="/user/workspaces" replace /> },
							{ path: "workspaces/:organisationId", element: <LegacyWorkspaceApps /> },
							{ path: "apps", element: <WorkspaceAppsPage />, ...handle("Apps") },
							{ path: "workspace-settings", element: <ConfigPage />, ...handle("Workspace settings") },
							{ path: "config", element: <Navigate to="../workspace-settings" replace /> },
							{ path: "account", element: <Navigate to="/user/settings/profile" replace /> },
							{ path: "account/*", element: <LegacyUserAccount /> },
							{ path: "platform", element: <Navigate to="../platform/system/mail/health" replace /> },
							{ path: "platform/*", element: <Legacy to="../platform/system" /> },
						],
					},
	
				{
					path: "hub",
					element: <AppGate app="hub" />,
					children: [
						{ index: true, element: <WorkspaceHubIndexRedirect /> },
						...MY_HUB_PAGES.map((page) => ({
							path: page.path,
							element: page.path === "my/tasks" ? <HubSection id="tasks" /> : <HubMine title={page.title} />,
							...handle(page.title),
						})),
						...HUB_PAGES.flatMap((page) => {
							const detail = page.path === "quotations"
								|| page.path === "sales-orders"
								|| page.path === "invoices"
								|| page.path === "receipts"
								|| page.path === "bills"
								|| page.path === "purchase-orders"
								|| page.path === "credits"
								|| page.path === "customer-sites"
								|| page.path === "projects"
								|| page.path === "inventory"
								|| page.path === "consumables";
							const pageElement = page.path === "tasks"
								? (
									<HubTeamTasksGate>
										<HubSection id={page.path} />
									</HubTeamTasksGate>
								)
								: (
									<HubStaffPageGate>
										<HubSection id={page.path} />
									</HubStaffPageGate>
								);
							const routes = detail
								? [
									...(page.path === "customer-sites" || page.path === "projects" || page.path === "inventory" || page.path === "consumables"
										? [{ path: `${page.path}/:docId`, element: <HubStaffPageGate><HubSection id={page.path} /></HubStaffPageGate>, ...handle(page.title) }]
										: [
											{ path: `${page.path}/:docId/edit`, element: <HubStaffPageGate><HubSection id={page.path} /></HubStaffPageGate>, ...handle(page.title) },
											{ path: `${page.path}/:docId`, element: <HubStaffPageGate><HubSection id={page.path} /></HubStaffPageGate>, ...handle(page.title) },
										]),
								]
								: [];
							return [...routes, { path: page.path, element: pageElement, ...handle(page.title) }];
						}),
						{ path: "work", element: <HubLegacyWorkRedirect /> },
						{ path: "operations", element: <Navigate to="inventory" replace /> },
						{ path: "sales", element: <Navigate to="leads" replace /> },
						{ path: "purchases", element: <Navigate to="expenses" replace /> },
						{ path: "people", element: <Navigate to="leave" replace /> },
						{ path: "finance", element: <Navigate to="transactions" replace /> },
						{ path: "admin", element: <Navigate to="maintenance" replace /> },
					],
				},
	
				{
					path: "mail",
					element: <AppGate app="mail" />,
					children: [{ index: true, element: <MailHome />, ...handle("Overview") }],
				},
	
				{
					path: "site",
					element: <AppGate app="site" />,
					children: [
						{ index: true, element: <SiteOverview />, ...handle("Overview") },
						{ path: "sites", element: <SiteList />, ...handle("Sites") },
						{ path: "sites/:siteId/files", element: <SiteFiles />, ...handle("Files") },
						{ path: "databases", element: <SiteDatabases />, ...handle("Databases") },
						{ path: "redirects", element: <SiteRedirects />, ...handle("Redirects") },
					],
				},
	
				{
					path: "team",
					element: <AppGate app="team" />,
					children: [
						{
							element: <TeamOverview />,
							children: [
								{ index: true, ...handle("Team") },
								{ path: "channels", element: <TeamChannels /> },
								{ path: "channels/:id", ...handle("Team") },
							],
						},
					],
				},
	
				{
					path: "social",
					element: <AppGate app="social" />,
					children: [
						{ index: true, element: <SocialOverview />, ...handle("Overview") },
						{ path: "channels", element: <SocialChannels />, ...handle("Channels") },
						{ path: "calendar", element: <SocialCalendar />, ...handle("Calendar") },
						{ path: "compose", element: <SocialCompose />, ...handle("Compose") },
						{ path: "results", element: <SocialResults />, ...handle("Results") },
					],
				},
	
				{
					path: "client",
					element: <AppGate app="client" />,
					children: [
						{ index: true, element: <ClientHome />, ...handle("Dashboard") },
						{ path: "work", element: <ClientWork />, ...handle("Work") },
						{ path: "calendar", element: <ClientCalendar />, ...handle("Calendar") },
						{ path: "quotations", element: <ClientQuotations />, ...handle("Quotations") },
						{ path: "invoices", element: <ClientInvoices />, ...handle("Invoices") },
						{ path: "leads", element: <Navigate to="work" replace /> },
						{ path: "deals", element: <Navigate to="work" replace /> },
						{ path: "organisation", element: <Navigate to="../hub/organisation" replace /> },
					],
				},
	
				{
					path: "phone",
					element: <AppGate app="phone" />,
					children: [
						{ index: true, element: <Navigate to="calls" replace /> },
						{ path: "calls", element: <PhoneCalls />, ...handle("Calls") },
						{ path: "sms", element: <PhoneSms />, ...handle("SMS") },
					],
				},

				{
					path: "engage",
					element: <AppGate app="engage" />,
					children: [
						{ index: true, element: <Navigate to="inbox" replace />, ...handle("Inbox") },
						{ path: "inbox", element: <EngageInbox />, ...handle("Inbox") },
						{ path: "inbox/:id", element: <EngageConversation />, ...handle("Chat") },
						{ path: "accounts", element: <EngageAccounts />, ...handle("Accounts") },
						{ path: "linkedin-sign-in/:loginId", element: <EngageLinkedInSignIn />, ...handle("LinkedIn") },
						{ path: "policy", element: <EngagePolicy />, ...handle("Policy") },
					],
				},
	
	
				],
			},

			{ path: "/manage", element: <WorkspaceLegacyRedirect /> },
			{ path: "/manage/*", element: <WorkspaceLegacyRedirect /> },
			{ path: "/hub", element: <WorkspaceLegacyRedirect /> },
			{ path: "/hub/*", element: <WorkspaceLegacyRedirect /> },
			{ path: "/mail", element: <WorkspaceLegacyRedirect /> },
			{ path: "/mail/*", element: <WorkspaceLegacyRedirect /> },
			{ path: "/site", element: <WorkspaceLegacyRedirect /> },
			{ path: "/site/*", element: <WorkspaceLegacyRedirect /> },
			{ path: "/team", element: <WorkspaceLegacyRedirect /> },
			{ path: "/team/*", element: <WorkspaceLegacyRedirect /> },
			{ path: "/social", element: <WorkspaceLegacyRedirect /> },
			{ path: "/social/*", element: <WorkspaceLegacyRedirect /> },
			{ path: "/client", element: <WorkspaceLegacyRedirect /> },
			{ path: "/client/*", element: <WorkspaceLegacyRedirect /> },
			{ path: "/phone", element: <WorkspaceLegacyRedirect /> },
			{ path: "/phone/*", element: <WorkspaceLegacyRedirect /> },
			{ path: "/engage", element: <WorkspaceLegacyRedirect /> },
			{ path: "/engage/*", element: <WorkspaceLegacyRedirect /> },
			{ path: "/domains/*", element: <Legacy to="/manage/domains" /> },
			{ path: "/domains", element: <Navigate to="/manage/domains" replace /> },
			{ path: "/mailboxes/*", element: <Legacy to="/manage/email/mailboxes" /> },
			{ path: "/mailboxes", element: <Navigate to="/manage/email/mailboxes" replace /> },
			{ path: "/aliases", element: <Navigate to="/manage/email/aliases" replace /> },
			{ path: "/forwarders", element: <Navigate to="/manage/email/forwarders" replace /> },
			{ path: "/migration", element: <Navigate to="/manage/email/migration" replace /> },
			{ path: "/mail/mailboxes/*", element: <Legacy to="/manage/email/mailboxes" /> },
			{ path: "/mail/mailboxes", element: <Navigate to="/manage/email/mailboxes" replace /> },
			{ path: "/mail/aliases", element: <Navigate to="/manage/email/aliases" replace /> },
			{ path: "/mail/forwarders", element: <Navigate to="/manage/email/forwarders" replace /> },
			{ path: "/mail/migration/*", element: <Legacy to="/manage/email/migration" /> },
			{ path: "/mail/migration", element: <Navigate to="/manage/email/migration" replace /> },
			{ path: "/phone/numbers", element: <Navigate to="/manage/phone/numbers" replace /> },
			{ path: "/hub/activity", element: <Navigate to="/manage/activity" replace /> },
			{ path: "/hub/integration", element: <Navigate to="/manage/integration" replace /> },
			{ path: "/billing", element: <Navigate to="/manage/billing" replace /> },
			{ path: "/settings/team", element: <Navigate to="/manage/access" replace /> },
			{ path: "/settings/organisation", element: <WorkspaceLegacyRedirect /> },
			{ path: "/settings/*", element: <Legacy to="/user/settings" /> },
			{ path: "/settings", element: <Navigate to="/user/settings/profile" replace /> },
			{ path: "/support", element: <Navigate to="/manage" replace /> },
			{ path: "/manage/mailboxes/*", element: <Legacy to="/manage/email/mailboxes" /> },
			{ path: "/manage/mailboxes", element: <Navigate to="/manage/email/mailboxes" replace /> },
			{ path: "/manage/aliases", element: <Navigate to="/manage/email/aliases" replace /> },
			{ path: "/manage/forwarders", element: <Navigate to="/manage/email/forwarders" replace /> },
			{ path: "/manage/migration/*", element: <Legacy to="/manage/email/migration" /> },
			{ path: "/manage/migration", element: <Navigate to="/manage/email/migration" replace /> },
			{ path: "/manage/settings/team", element: <Navigate to="/manage/team" replace /> },
			{ path: "/manage/team/access", element: <Navigate to="/manage/team" replace /> },
			{ path: "/manage/settings", element: <WorkspaceLegacyRedirect /> },
			{ path: "/manage/support", element: <Navigate to="/manage" replace /> },
			{ path: "/dash", element: <Navigate to="/platform/system/mail/health" replace /> },
			{ path: "/dash/mail-health", element: <Navigate to="/platform/system/mail/health" replace /> },
			{ path: "/dash/queues", element: <Navigate to="/platform/system/mail/queues" replace /> },
			{ path: "/dash/abuse", element: <Navigate to="/platform/system/mail/abuse" replace /> },
			{ path: "/dash/audit", element: <Navigate to="/platform/system/audit" replace /> },
			{ path: "/dash/configuration", element: <Navigate to="/platform/system/mail/server" replace /> },
			{ path: "/dash/support", element: <Navigate to="/manage" replace /> },
			{ path: "/dash/incidents", element: <Navigate to="/manage" replace /> },
			{ path: "*", element: <NotFound />, ...handle("Not found") },
		],
	},
], basename ? { basename } : undefined);
